import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, MapPin, CalendarDays } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';
import { isPastEvent, formatEventCountdown } from '@/utils/eventCountdown';

const _TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

const EventsPublicPage = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = _TABS.some((t) => t.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'upcoming';

  useEffect(() => {
    (async () => {
      try {
        // Bundle 155 - fetch upcoming + past in parallel. The
        // backend caps `limit` at 100 and sorts start_datetime
        // asc by default; the `past_only=true` flag flips the
        // sort direction so the most-recent ended events land
        // on the first page (see event_routes.py L14).
        const [upRes, paRes] = await Promise.all([
          api.get('/events', { params: { limit: 100 } }),
          api.get('/events', { params: { past_only: true, limit: 100 } }),
        ]);
        setUpcoming((upRes.data.events || [])
          .filter((e) => !isPastEvent(e)));
        setPast(paRes.data.events || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const list = tab === 'past' ? past : upcoming;

  const selectTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'upcoming') params.delete('tab');
    else params.set('tab', key);
    setSearchParams(params, { replace: false });
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-16">
      <div className="max-w-2xl mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-3">
          {tab === 'past' ? 'Archive' : 'Upcoming'}
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-900 mb-4">
          Events
        </h1>
        <p className="text-neutral-600 leading-relaxed">
          Small, thoughtful gatherings and travel experiences - curated for real conversation and warm introductions.
        </p>
      </div>

      {/* Bundle 155 - upcoming / past tab strip. URL-persisted
          via ?tab=past. Counts reflect the client-side split. */}
      <div
        className="mb-8 inline-flex items-center gap-1 rounded-full bg-neutral-100 p-1"
        role="tablist"
        aria-label="Events by time"
        data-testid="events-tabs"
      >
        {_TABS.map((t) => {
          const count = t.key === 'past' ? past.length : upcoming.length;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectTab(t.key)}
              data-testid={`events-tab-${t.key}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                active
                  ? 'bg-white text-primary-900 shadow-sm'
                  : 'text-neutral-500 hover:text-primary-900'
              }`}
            >
              {t.label}
              <span
                className={`ml-2 text-[11px] font-normal ${active ? 'text-neutral-500' : 'text-neutral-400'}`}
                data-testid={`events-tab-${t.key}-count`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" />
        </div>
      ) : list.length === 0 ? (
        <Card className="border shadow-soft">
          <CardContent className="p-12 text-center">
            <Sparkles className="h-8 w-8 text-secondary-500 mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold text-primary-900 mb-2">
              {tab === 'past'
                ? 'No past events yet'
                : 'Nothing published yet'}
            </h3>
            <p className="text-neutral-600">
              {tab === 'past'
                ? 'Once an event wraps, it will show up here with the recap.'
                : 'Check back soon - the next Dostination gathering is being finalized.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          data-testid={`events-grid-${tab}`}
        >
          {list.map((e) => (
            <PublicEventTile key={e.id} event={e} isPast={tab === 'past'} />
          ))}
        </div>
      )}
    </div>
  );
};

const PublicEventTile = ({ event, isPast }) => {
  const { trackEvent } = usePixels();
  const date = event.start_datetime || event.start_date;
  const _tzOpt = (() => {
    const tz = event.timezone;
    if (!tz) return {};
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
      return { timeZone: tz };
    } catch { return {}; }
  })();
  const dateLabel = date ? new Date(date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', ..._tzOpt,
  }) : 'Date TBD';
  const city = event.city || event.location?.city;
  const country = event.country || event.location?.country;
  const locLabel = [city, country].filter(Boolean).join(', ') || ' - ';

  // Bundle 155 - render a "Past event" chip on past cards so
  // users see at a glance which tab they landed in. Uses the
  // shared formatter to derive the label consistently with the
  // detail page.
  const countdown = formatEventCountdown(
    date, { endDate: event.end_datetime || event.end_date });

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group"
      onClick={() => trackEvent('Clicked on Event Card', {
        content_name: event.title,
        content_ids: [event.slug || event.id],
      })}
      data-testid={`events-tile-${event.slug || event.id}`}
    >
      <Card className={`border shadow-soft group-hover:shadow-gold transition-all duration-300 h-full overflow-hidden ${isPast ? 'opacity-90' : ''}`}>
        <div className="aspect-[16/9] bg-gradient-to-br from-primary-600 to-primary-900 relative">
          {(event.hero_image_list_url || event.hero_image_url) ? (
            <img src={event.hero_image_list_url || event.hero_image_url} alt={event.title} className={`w-full h-full object-cover ${isPast ? 'grayscale-[35%]' : ''}`} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-secondary-300">
              <Sparkles className="h-10 w-10" />
            </div>
          )}
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-primary-900 text-xs font-semibold">
              <CalendarDays className="h-3 w-3" /> {dateLabel}
            </span>
          </div>
          {isPast && (
            <div className="absolute top-3 right-3">
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900/70 text-white text-[10px] font-semibold uppercase tracking-widest"
                data-testid={`events-tile-past-chip-${event.slug || event.id}`}
              >
                {countdown.label || 'Past event'}
              </span>
            </div>
          )}
        </div>
        <CardContent className="p-5">
          <h3 className="font-display text-xl font-semibold text-primary-900 mb-1 line-clamp-1">{event.title}</h3>
          {event.tagline && <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mb-3">{event.tagline}</p>}
          <div className="flex items-center gap-1 text-xs text-neutral-500">
            <MapPin className="h-3.5 w-3.5 text-secondary-600" /> {locLabel}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default EventsPublicPage;
